// ShopProductPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  Container,
  Grid,
  Avatar,
  Rating,
  Tabs,
  Tab,
  Skeleton,
  Breadcrumbs,
  Link,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import {
  ShoppingCartOutlined,
  FavoriteBorder,
  Favorite,
  Add,
  Remove,
  Share,
  LocalShippingOutlined,
  VerifiedOutlined,
  SupportAgentOutlined,
  ArrowBack,
  CheckCircleOutline,
  NavigateNext,
  SendOutlined,
  StarOutlined,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCart } from "../../context/CartContext";
import configLocal from "../../config/config.local.json";

const BASE_URL = configLocal.baseURL;

/* ─── Brand tokens ─────────────────────────────────────── */
const brand = {
  navy: "#2E4053",
  navyDark: "#1C2A3D",
  navyLight: "#4A6177",
  silverBlue: "#D1D8E0",
  silverLight: "#EDF0F3",
  textLight: "#34495E",
  muted: "#A6AAAD",
  white: "#FFFFFF",
  danger: "#E74C3C",
  gold: "#C9A84C",
  success: "#27AE60",
};

/* ─── Subdomain helper ────────────────────────────────── */
//TODO: add image to reviews
//TODO: add points  to reviews
//TODO: dont show place to submit review if user submitted before
//TODO: show "verified purchase" badge on reviews if user purchased the product before
//TODO: add "helpful" button to reviews and show helpful count

/* ─── Stock badge ─────────────────────────────────────── */
const StockBadge = ({ qty }) => {
  if (qty <= 0)
    return (
      <Chip
        label="OUT OF STOCK"
        size="small"
        sx={{
          bgcolor: brand.danger,
          color: "#fff",
          fontWeight: 900,
          fontSize: "0.68rem",
        }}
      />
    );
  if (qty <= 5)
    return (
      <Chip
        label={`LOW STOCK — ${qty} left`}
        size="small"
        sx={{
          bgcolor: "#F39C12",
          color: "#fff",
          fontWeight: 900,
          fontSize: "0.68rem",
        }}
      />
    );
  return (
    <Chip
      label="IN STOCK"
      size="small"
      sx={{
        bgcolor: brand.success,
        color: "#fff",
        fontWeight: 900,
        fontSize: "0.68rem",
      }}
    />
  );
};

/* ─── Feature strip ───────────────────────────────────── */
const FEATURES = [
  { icon: <LocalShippingOutlined />, label: "Fast Delivery" },
  { icon: <VerifiedOutlined />, label: "Verified Quality" },
  { icon: <SupportAgentOutlined />, label: "24/7 Support" },
];

/* ─── Rating breakdown bar ───────────────────────────── */
const RatingBar = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: brand.muted,
          fontFamily: "Poppins",
          width: 10,
        }}
      >
        {star}
      </Typography>
      <Box
        sx={{
          width: 110,
          height: 6,
          bgcolor: brand.silverBlue,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: brand.gold,
            borderRadius: 3,
            transition: "width 0.6s ease",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: "0.72rem",
          color: brand.muted,
          fontFamily: "Poppins",
          width: 28,
        }}
      >
        {pct}%
      </Typography>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════
                    MAIN COMPONENT
════════════════════════════════════════════════════════ */
const ShopProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const shopParam = "test.spadeplatform.com";

  /* product */
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [qty, setQty] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);
  const [tab, setTab] = useState(0);

  /* reviews */
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewPages, setReviewPages] = useState(1);
  const [reviewSort, setReviewSort] = useState("newest");
  const [reviewsLoading, setReviewsLoading] = useState(false);

  /* write-review form */
  const [myRating, setMyRating] = useState(0);
  const [myTitle, setMyTitle] = useState("");
  const [myBody, setMyBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  /* ── fetch product ── */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/public/shop/products/${id}?shop=${shopParam}`
        );
        setProduct(res.data.data);
      } catch (err) {
        console.error(err);
        toast.error("Could not load product details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  /* ── fetch reviews (only when Reviews tab is active) ── */
  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/public/shop/products/${id}/reviews`,
        {
          params: {
            shop: shopParam,
            page: reviewPage,
            limit: 5,
            sort: reviewSort,
          },
        }
      );
      setReviews(res.data.data || []);
      setReviewStats(res.data.stats || null);
      setReviewPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setReviewsLoading(false);
    }
  }, [id, reviewPage, reviewSort]);

  useEffect(() => {
    if (tab === 2) loadReviews();
  }, [tab, loadReviews]);

  /* ── submit review ── */
  const handleSubmitReview = async () => {
    if (!myRating) return toast.error("Please select a star rating.");

    if (myBody.trim().length < 5)
      return toast.error("Review is too short (min 5 characters).");

    const token = localStorage.getItem("token");

    if (!token) {
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      toast.error("Please log in to leave a review.");
      setTimeout(() => navigate("/shop/customerlogin"), 1000);
      return;
    }

    setSubmitting(true);
    console.log("Submitting review with:", { myRating, myTitle, myBody });
    try {
      const res = await axios.post(
        `${BASE_URL}/api/public/shop/products/${id}/reviews?shop=${shopParam}`,
        {
          rating: myRating,
          title: myTitle,
          body: myBody,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Review submission response:", res);
      switch (res.data.messageId) {
        case 0:
          toast.success(
            "Review submitted successfully. It will appear after approval."
          );

          setMyRating(0);
          setMyTitle("");
          setMyBody("");
          break;
        default:
          toast.info(res.data.message || "Review submitted.");
          break;
      }
      setMyRating(0);
      setMyTitle("");
      setMyBody("");
    } catch (err) {
      const messageId = err.response?.data?.messageId;
      switch (messageId) {
        case 1: // Login required
          toast.error("Please log in to leave a review.");
          localStorage.setItem("redirectAfterLogin", window.location.pathname);
          setTimeout(() => navigate("/shop/customerlogin"), 1000);
          break;
        case 3: // Validation error
          toast.error(err.response.data.message || "Invalid review input.");
          break;
        case 4: // Product not found
          toast.error("Product not found.");
          break;
        case 5: // Duplicate review
          toast.error("You have already reviewed this product.");
          break;
        case 6: //  review
          toast.error("You can only review products you have purchased.");
          break;
        case -1: // Server error
          toast.error("Server error. Please try again later.");
          console.error(err.response?.data?.details || err);
          break;
        default:
          toast.error(
            err.response?.data?.message || "Failed to submit review."
          );
          break;
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handleAddToCart = () => {
    if (!product) return;
    for (let i = 0; i < qty; i++) addToCart(product, qty);

    toast.success(`${qty}× product added to cart!`);
  };
  const handleWishlist = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        toast.error("Please login first");
        navigate("/shop/customerlogin");
        return;
      }

      await axios.post(
        `${BASE_URL}/api/public/wishlist/add?shop=${shopParam}`,
        { product_id: product.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWishlisted((w) => !w);
      toast.success(wishlisted ? "Removed from wishlist" : "Added to wishlist");
    } catch (err) {
      console.error(err);
      toast.error("Wishlist failed");
    }
  };
  const discountedPrice =
    product?.discount > 0
      ? (product.selling_price * (1 - product.discount / 100)).toFixed(2)
      : null;

  /* ── loading skeleton ── */
  if (loading) {
    return (
      <Box sx={{ bgcolor: "#F4F7F9", minHeight: "100vh", py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rounded"
                height={480}
                sx={{ borderRadius: 4 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {[120, 60, 80, 40, 100, 50, 60].map((w, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={`${w}%`}
                  height={32}
                  sx={{ mb: 1 }}
                />
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!product) return null;
  const isOutOfStock = product.quantity_in_stock <= 0;

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  return (
    <Box
      sx={{
        bgcolor: "#F4F7F9",
        minHeight: "100vh",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        {/* Breadcrumb */}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 4, "& *": { fontFamily: "Poppins", fontSize: "0.82rem" } }}
        >
          <Link
            underline="hover"
            color={brand.muted}
            onClick={() => navigate("/shop/products")}
            sx={{ cursor: "pointer" }}
          >
            Products
          </Link>
          {product.category_name && (
            <Link
              underline="hover"
              color={brand.muted}
              sx={{ cursor: "pointer" }}
            >
              {product.category_name}
            </Link>
          )}
          <Typography
            sx={{
              color: brand.navy,
              fontWeight: 700,
              fontSize: "0.82rem",
              fontFamily: "Poppins",
            }}
          >
            {product.name}
          </Typography>
        </Breadcrumbs>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false} // Prevents sticking when switching tabs
          draggable
          pauseOnHover
        />
        {/* ════════ HERO ════════ */}
        <Grid container spacing={{ xs: 4, md: 7 }} alignItems="flex-start">
          {/* LEFT — Image panel */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                borderRadius: "20px",
                overflow: "hidden",
                bgcolor: brand.white,
                border: `1px solid ${brand.silverBlue}`,
                aspectRatio: "1/1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 40px rgba(46,64,83,0.10)",
              }}
            >
              <Box sx={{ position: "absolute", top: 20, left: 20, zIndex: 3 }}>
                <StockBadge qty={product.quantity_in_stock} />
              </Box>
              {product.discount > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    zIndex: 3,
                    bgcolor: brand.danger,
                    color: "#fff",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 900,
                    fontSize: "0.85rem",
                    fontFamily: "Poppins",
                  }}
                >
                  -{product.discount}%
                </Box>
              )}
              <IconButton
                onClick={handleWishlist}
                sx={{
                  position: "absolute",
                  bottom: 20,
                  right: 20,
                  zIndex: 3,
                  bgcolor: brand.white,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  "&:hover": { bgcolor: "#FFF0F0" },
                }}
              >
                {wishlisted ? (
                  <Favorite sx={{ color: brand.danger }} />
                ) : (
                  <FavoriteBorder sx={{ color: brand.muted }} />
                )}
              </IconButton>
              <IconButton
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: product.name,
                      text: "Check this product",
                      url: window.location.href,
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    toast.info("Link copied!");
                  }
                }}
                sx={{
                  position: "absolute",
                  bottom: 20,
                  left: 20,
                  zIndex: 3,
                  bgcolor: brand.white,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  "&:hover": { bgcolor: brand.silverLight },
                }}
              >
                <Share sx={{ color: brand.muted }} />
              </IconButton>
              {!imgLoaded && (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    transform: "none",
                  }}
                />
              )}
              <Box
                component="img"
                src={product.photo || "https://via.placeholder.com/500"}
                alt={product.name}
                onLoad={() => setImgLoaded(true)}
                sx={{
                  width: "88%",
                  height: "88%",
                  objectFit: "contain",
                  opacity: imgLoaded ? 1 : 0,
                  transition: "opacity 0.4s ease",
                  filter: isOutOfStock ? "grayscale(80%)" : "none",
                }}
              />
            </Box>

            {/* Feature strip */}
            <Box
              sx={{
                mt: 3,
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                bgcolor: brand.white,
                borderRadius: "14px",
                border: `1px solid ${brand.silverBlue}`,
                p: 2,
              }}
            >
              {FEATURES.map((f) => (
                <Box
                  key={f.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ color: brand.navy }}>{f.icon}</Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: brand.textLight,
                      fontFamily: "Poppins",
                      textAlign: "center",
                    }}
                  >
                    {f.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* RIGHT — Info panel */}
          <Grid item xs={12} md={6}>
            {/* SKU + category */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                label={`SKU: ${
                  product.sku ||
                  product.id.toString().substring(0, 8).toUpperCase()
                }`}
                size="small"
                sx={{
                  fontFamily: "'Roboto Mono', monospace",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  bgcolor: "#EDF0F3",
                  color: brand.muted,
                }}
              />
              {product.category_name && (
                <Chip
                  label={product.category_name}
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    bgcolor: brand.navyDark,
                    color: "#fff",
                  }}
                />
              )}
            </Box>

            {/* Name */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: brand.navy,
                fontFamily: "Poppins",
                lineHeight: 1.2,
                mb: 1,
              }}
            >
              {product.name}
              {product.variant_name && (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 400,
                    color: brand.muted,
                    fontSize: "1.1rem",
                    ml: 1,
                  }}
                >
                  — {product.variant_name}
                </Box>
              )}
            </Typography>

            {/* Stars */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <Rating
                value={reviewStats?.average_rating || 0}
                precision={0.5}
                size="small"
                readOnly
                sx={{ color: brand.gold }}
              />
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: brand.muted,
                  fontFamily: "Poppins",
                }}
              >
                {reviewStats
                  ? `${reviewStats.average_rating ?? "—"} (${
                      reviewStats.total
                    } reviews)`
                  : "No reviews yet"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: brand.navyLight,
                  fontFamily: "Poppins",
                  ml: "auto",
                  fontWeight: 700,
                }}
              >
                {product.number_sold || 0} sold
              </Typography>
            </Box>

            {/* Price block */}
            <Box
              sx={{
                bgcolor: brand.silverLight,
                borderRadius: "14px",
                p: 3,
                mb: 3,
                border: `1px solid ${brand.silverBlue}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: brand.muted,
                  fontWeight: 700,
                  fontFamily: "Poppins",
                  letterSpacing: 1,
                }}
              >
                UNIT PRICE
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1.5,
                  mt: 0.5,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: brand.navy,
                    fontFamily: "Poppins",
                  }}
                >
                  {discountedPrice || product.selling_price}
                  <Box
                    component="span"
                    sx={{ fontSize: "1rem", ml: 0.5, color: brand.muted }}
                  >
                    {product.currency || "EGP"}
                  </Box>
                </Typography>
                {discountedPrice && (
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      color: brand.muted,
                      textDecoration: "line-through",
                      fontFamily: "Poppins",
                    }}
                  >
                    {product.selling_price} {product.currency || "EGP"}
                  </Typography>
                )}
              </Box>
              {product.tax_rate > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: brand.muted,
                    mt: 0.5,
                    fontFamily: "Poppins",
                  }}
                >
                  Incl. {product.tax_rate}% tax
                </Typography>
              )}
            </Box>

            {/* Qty + Add to Cart */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: `2px solid ${brand.silverBlue}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                  bgcolor: brand.white,
                }}
              >
                <IconButton
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={isOutOfStock}
                  size="small"
                  sx={{ borderRadius: 0, px: 1.5, color: brand.navy }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <Typography
                  sx={{
                    minWidth: 36,
                    textAlign: "center",
                    fontWeight: 800,
                    fontFamily: "Poppins",
                    color: brand.navy,
                    fontSize: "1rem",
                  }}
                >
                  {qty}
                </Typography>
                <IconButton
                  onClick={() =>
                    setQty((q) =>
                      Math.min(product.quantity_in_stock || 99, q + 1)
                    )
                  }
                  disabled={isOutOfStock}
                  size="small"
                  sx={{ borderRadius: 0, px: 1.5, color: brand.navy }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                startIcon={<ShoppingCartOutlined />}
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                sx={{
                  flex: 1,
                  bgcolor: brand.navy,
                  color: "#fff",
                  fontWeight: 700,
                  fontFamily: "Poppins",
                  py: 1.5,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { bgcolor: brand.navyDark },
                  "&.Mui-disabled": {
                    bgcolor: brand.silverBlue,
                    color: brand.muted,
                  },
                }}
              >
                {isOutOfStock ? "Out of Stock" : "Add to Cart"}
              </Button>
            </Box>

            {/* Quick pills */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {[
                product.quantity_in_stock > 0 && {
                  icon: <CheckCircleOutline sx={{ fontSize: 16 }} />,
                  label: `${product.quantity_in_stock} in stock`,
                },
                product.warehouse_location && {
                  icon: null,
                  label: `📦 ${product.warehouse_location}`,
                },
              ]
                .filter(Boolean)
                .map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: brand.white,
                      border: `1px solid ${brand.silverBlue}`,
                      borderRadius: "20px",
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.78rem",
                      fontFamily: "Poppins",
                      color: brand.textLight,
                      fontWeight: 600,
                    }}
                  >
                    {s.icon}
                    {s.label}
                  </Box>
                ))}
            </Box>
          </Grid>
        </Grid>

        {/* ════════ TABS ════════ */}
        <Box
          sx={{
            mt: 8,
            bgcolor: brand.white,
            borderRadius: "20px",
            border: `1px solid ${brand.silverBlue}`,
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(46,64,83,0.07)",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: `1px solid ${brand.silverBlue}`,
              "& .MuiTab-root": {
                fontFamily: "Poppins",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.95rem",
                color: brand.muted,
              },
              "& .Mui-selected": { color: `${brand.navy} !important` },
              "& .MuiTabs-indicator": { bgcolor: brand.navy, height: 3 },
            }}
          >
            <Tab label="Description" />
            <Tab label="Specifications" />
            <Tab
              label={`Reviews${reviewStats ? ` (${reviewStats.total})` : ""}`}
            />
          </Tabs>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {/* ── Description ── */}
            {tab === 0 && (
              <Typography
                sx={{
                  color: brand.textLight,
                  lineHeight: 1.9,
                  fontFamily: "Poppins",
                  fontSize: "0.95rem",
                  maxWidth: 720,
                }}
              >
                {product.description ||
                  "No description available for this product."}
              </Typography>
            )}

            {/* ── Specifications ── */}
            {tab === 1 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  maxWidth: 700,
                }}
              >
                {[
                  ["SKU", product.sku || "—"],
                  ["Category", product.category_name || "—"],

                  [
                    "Discount",
                    product.discount > 0 ? `${product.discount}%` : "None",
                  ],
                  ["Qty in Stock", product.quantity_in_stock],

                  ["Warehouse", product.warehouse_location || "—"],
                  ["Status", product.status || "active"],
                  [
                    "Last Sold",
                    product.last_sold_at
                      ? new Date(product.last_sold_at).toLocaleDateString()
                      : "—",
                  ],
                ].map(([label, value], i) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.5,
                      bgcolor: i % 2 === 0 ? brand.silverLight : brand.white,
                      borderBottom: `1px solid ${brand.silverBlue}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        color: brand.muted,
                        fontFamily: "Poppins",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: brand.navy,
                        fontFamily: "Poppins",
                        textAlign: "right",
                      }}
                    >
                      {String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── Reviews ── */}
            {tab === 2 && (
              <Box>
                {/* Summary */}
                {reviewStats && reviewStats.total > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      mb: 5,
                      p: 3,
                      bgcolor: brand.silverLight,
                      borderRadius: "14px",
                      border: `1px solid ${brand.silverBlue}`,
                      maxWidth: 420,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "3.5rem",
                          fontWeight: 900,
                          color: brand.navy,
                          fontFamily: "Poppins",
                          lineHeight: 1,
                        }}
                      >
                        {reviewStats.average_rating ?? "—"}
                      </Typography>
                      <Rating
                        value={reviewStats.average_rating || 0}
                        precision={0.5}
                        size="small"
                        readOnly
                        sx={{ color: brand.gold }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: brand.muted,
                          fontFamily: "Poppins",
                        }}
                      >
                        {reviewStats.total} reviews
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      {[5, 4, 3, 2, 1].map((star) => (
                        <RatingBar
                          key={star}
                          star={star}
                          count={reviewStats.breakdown[star] || 0}
                          total={reviewStats.total}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Sort + list */}
                {reviewsLoading ? (
                  <Box
                    sx={{ py: 4, display: "flex", justifyContent: "center" }}
                  >
                    <CircularProgress sx={{ color: brand.navy }} size={36} />
                  </Box>
                ) : (
                  <>
                    {reviews.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 3,
                        }}
                      >
                        <Select
                          value={reviewSort}
                          onChange={(e) => {
                            setReviewSort(e.target.value);
                            setReviewPage(1);
                          }}
                          size="small"
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "0.82rem",
                            minWidth: 160,
                          }}
                        >
                          <MenuItem value="newest">Newest first</MenuItem>
                          <MenuItem value="oldest">Oldest first</MenuItem>
                          <MenuItem value="highest">Highest rated</MenuItem>
                          <MenuItem value="lowest">Lowest rated</MenuItem>
                        </Select>
                      </Box>
                    )}

                    {reviews.length === 0 && (
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <StarOutlined
                          sx={{ fontSize: 48, color: brand.silverBlue, mb: 1 }}
                        />
                        <Typography
                          sx={{ color: brand.muted, fontFamily: "Poppins" }}
                        >
                          No approved reviews yet. Be the first!
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      {reviews.map((review) => (
                        <Box
                          key={review.id}
                          sx={{
                            p: 3,
                            borderRadius: "14px",
                            border: `1px solid ${brand.silverBlue}`,
                            bgcolor: brand.white,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: brand.navy,
                                color: "#fff",
                                fontWeight: 900,
                                fontFamily: "Poppins",
                                width: 40,
                                height: 40,
                                fontSize: "0.85rem",
                              }}
                            >
                              {(review.customer_name || "?")
                                .substring(0, 2)
                                .toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontFamily: "Poppins",
                                  color: brand.navy,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {review.customer_name || "Anonymous"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: brand.muted,
                                  fontFamily: "Poppins",
                                }}
                              >
                                {new Date(review.created_at).toLocaleDateString(
                                  "en-US",
                                  { month: "long", year: "numeric" }
                                )}
                              </Typography>
                            </Box>
                            <Rating
                              value={review.rating}
                              size="small"
                              readOnly
                              sx={{ color: brand.gold, ml: "auto" }}
                            />
                          </Box>
                          {review.title && (
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontFamily: "Poppins",
                                color: brand.navy,
                                fontSize: "0.88rem",
                                mb: 0.5,
                              }}
                            >
                              {review.title}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              color: brand.textLight,
                              fontFamily: "Poppins",
                              fontSize: "0.88rem",
                              lineHeight: 1.7,
                            }}
                          >
                            {review.body}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Pagination */}
                    {reviewPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                          mt: 4,
                        }}
                      >
                        <Button
                          disabled={reviewPage <= 1}
                          onClick={() => setReviewPage((p) => p - 1)}
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: brand.navy,
                            textTransform: "none",
                          }}
                        >
                          ← Prev
                        </Button>
                        <Typography
                          sx={{
                            alignSelf: "center",
                            fontFamily: "Poppins",
                            color: brand.muted,
                            fontSize: "0.85rem",
                          }}
                        >
                          {reviewPage} / {reviewPages}
                        </Typography>
                        <Button
                          disabled={reviewPage >= reviewPages}
                          onClick={() => setReviewPage((p) => p + 1)}
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 700,
                            color: brand.navy,
                            textTransform: "none",
                          }}
                        >
                          Next →
                        </Button>
                      </Box>
                    )}
                  </>
                )}

                {/* Write a review */}
                <Divider sx={{ my: 5 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontFamily: "Poppins",
                    color: brand.navy,
                    mb: 3,
                  }}
                >
                  Write a Review
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    maxWidth: 600,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        color: brand.textLight,
                        mb: 0.5,
                      }}
                    >
                      Your Rating *
                    </Typography>
                    <Rating
                      value={myRating}
                      onChange={(_, v) => setMyRating(v)}
                      size="large"
                      sx={{ color: brand.gold }}
                    />
                  </Box>
                  <TextField
                    label="Title (optional)"
                    value={myTitle}
                    onChange={(e) => setMyTitle(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        { borderColor: brand.navy },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: brand.navy,
                      },
                      "& *": { fontFamily: "Poppins" },
                    }}
                  />
                  <TextField
                    label="Your Review *"
                    value={myBody}
                    onChange={(e) => setMyBody(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        { borderColor: brand.navy },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: brand.navy,
                      },
                      "& *": { fontFamily: "Poppins" },
                    }}
                  />
                  <Button
                    variant="contained"
                    endIcon={
                      submitting ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <SendOutlined />
                      )
                    }
                    onClick={handleSubmitReview}
                    disabled={submitting}
                    sx={{
                      alignSelf: "flex-start",
                      bgcolor: brand.navy,
                      color: "#fff",
                      fontFamily: "Poppins",
                      fontWeight: 700,
                      textTransform: "none",
                      px: 4,
                      py: 1.2,
                      borderRadius: "10px",
                      "&:hover": { bgcolor: brand.navyDark },
                    }}
                  >
                    {submitting ? "Submitting…" : "Submit Review"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Back */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/shop/products")}
          sx={{
            mt: 5,
            color: brand.navy,
            fontFamily: "Poppins",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { bgcolor: brand.silverLight },
          }}
        >
          Back to Products
        </Button>
      </Container>
    </Box>
  );
};

export default ShopProductPage;
